import IconButton from '@/components/ui/custom/buttons/IconButton';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import useToggle from '@/data/hook/useToggle';
import {
  deleteLessonIntoBlock,
  updateLessonIntoBlock,
} from '@/data/services/lessonBlocksServices';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import CoursePath from '@/models/CoursePath';
import Lesson from '@/models/Lesson';
import LessonBlock from '@/models/_deprecated/LessonBlock';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  getBoxPathname,
  getNextLesson,
  getPreviousLesson,
} from '@/utils/lesson/box';
import alert from '@/utils/shared/alert';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import useLessonBox from '../LessonBox.hooks';

type DeleteActionProps = {
  lesson: Lesson;
  coursePath: CoursePath;
  block?: LessonBlock;
};

export default function DeleteAction({
  lesson,
  coursePath,
  block,
}: DeleteActionProps) {
  const { isOpen, toggle, close } = useToggle();
  const { lessonsInBlockCacheManager, lessons } = useLessonBox();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseBox.actions',
  });

  const handleNavigate = () => {
    const previousLesson = getPreviousLesson(lessons, lesson.id);
    const nextLesson = getNextLesson(lessons, lesson.id);
    const lessonToNavigate = previousLesson || nextLesson;
    navigate(
      getBoxPathname(coursePath.slug, lessonToNavigate?.order?.toString()),
    );
  };

  const handleDelete = async () => {
    let auxLesson: Lesson = lesson;
    if (!block) return;
    if (lesson.status === VersioningStatusEnum.PUBLISHED) {
      auxLesson = await updateLessonIntoBlock(
        coursePath.slug,
        block.id,
        auxLesson.id,
        {},
      );
    }
    await deleteLessonIntoBlock(coursePath.slug, block.id, auxLesson.id);
  };
  const { mutate, isPending: isDiscarding } = useMutation(handleDelete, {
    onSuccess() {
      lessonsInBlockCacheManager.remove(lesson.id);
      handleNavigate();
    },
    onError(error) {
      alert.error(getErrorMessage(error));
    },
    onSettled() {
      toggle();
    },
  });
  return (
    <Fragment>
      <ModalDisable
        isRequesting={isDiscarding}
        modalType="delete"
        visible={isOpen}
        selectedObjectName={lesson.name}
        translationString={'modalDisableLesson'}
        onClickConfirm={() => mutate()}
        onClickCancel={close}
      />
      <IconButton
        tooltip={t('discard')}
        color="custom"
        onClick={toggle}
        className="w-5 h-5 text-error"
        icon={<TrashIcon />}
      />
    </Fragment>
  );
}
